import React, { useState, useEffect } from 'react';
import { useForm } from "react-hook-form";
import Loading from "../components/Loading";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import UserService from "../services/UserService";
import { PageLoader } from "./PageLoader";
import '../styles/components/admin-data-sync.css';

export const AdminDataSync = () => {
    // React Hook Form setup
    const { register, handleSubmit, reset } = useForm();

    // State for user and UI feedback
    const [farelanesUser, setFarelanesUser] = useState(null);
    const [state, setState] = useState({
        loading: false,
        content: null,
        message: '',
        error: null,
    });

    const { getAccessTokenSilently, user } = useAuth0();

    // Fetch user info and initialize form defaults
    useEffect(() => {
        const fetchUser = async () => {
            try {
                const token = await getAccessTokenSilently();
                const response = await UserService.me(token, user);
                setFarelanesUser({
                    firstName: response.data.auth0GivenName,
                    lastName: response.data.auth0FamilyName,
                    numberOfDays: '31',
                    milesGreaterThan: '99',
                    lineHaulAmountGreaterThan: '100',
                    lineHaulAmountLessThan: '30000',
                });
            } catch (error) {
                setState(prev => ({ ...prev, error: error?.error || 'Failed to load user info.' }));
            }
        };
        fetchUser();
    }, [getAccessTokenSilently, user]);

    // Reset form when user data is loaded
    useEffect(() => {
        if (farelanesUser) reset(farelanesUser);
    }, [farelanesUser, reset]);

    // Handle form submission
    const onSubmit = async (data) => {
        setState(prev => ({ ...prev, loading: true, message: '', error: null, content: null }));
        try {
            const token = await getAccessTokenSilently();
            const response = await UserService.doProductionDataIngestion(
                data.numberOfDays,
                data.milesGreaterThan,
                data.lineHaulAmountLessThan,
                data.lineHaulAmountGreaterThan,
                token
            );
            setState(prev => ({ ...prev, loading: false, content: response.data, message: 'Success!' }));
        } catch (error) {
            setState(prev => ({
                ...prev,
                loading: false,
                error: error?.response?.data?.message || 'Operation failed.',
            }));
        }
    };

    return (
        <div className="admin-data-sync-container">
            <div className="warning-section">
                <small>This operation deletes all production lane data and creates a new collection using the below filters.</small>
                <small>If you are a super admin, and unsure about the below operation - Please check with Andrew Cohen before running the job</small>
            </div>
            
            {farelanesUser ? (
                <form onSubmit={handleSubmit(onSubmit)} className="admin-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label>No. of Days</label>
                            <input name="numberOfDays" type="text" {...register('numberOfDays')} className="form-control" />
                        </div>
                        <div className="form-group">
                            <label>Miles Greater Than</label>
                            <input name="milesGreaterThan" type="text" {...register('milesGreaterThan')} className="form-control" />
                        </div>
                        <div className="form-group">
                            <label>LineHaul $ Greater Than</label>
                            <input name="lineHaulAmountGreaterThan" type="text" {...register('lineHaulAmountGreaterThan')} className="form-control" />
                        </div>
                        <div className="form-group">
                            <label>LineHaul $ Less Than</label>
                            <input name="lineHaulAmountLessThan" type="text" {...register('lineHaulAmountLessThan')} className="form-control" />
                        </div>
                    </div>
                    
                    <div className="form-group button-container">
                        <button type="submit" className="query-button" disabled={state.loading}>
                            {state.loading && <PageLoader />}
                            <span>RUN JOB</span>
                        </button>
                    </div>
                    
                    {state.error && (
                        <div className="alert alert-danger" role="alert">
                            {state.error}
                        </div>
                    )}
                    
                    {state.content && (
                        <div className="card">
                            <div className="card-header">
                                <h5>Operation Successful!</h5>
                            </div>
                            <div className="card-body">
                                <h5 className="card-title">{state.content.recordsFound} <small>records found in General Lane Data for the last {state.content.numberOfDays || '-'} days from now</small></h5>
                                <h5 className="card-title">{state.content.recordsFiltered} <small>records updated with the above filters</small></h5>
                                <p className="card-text">Production lane data has been successfully recreated.</p>
                                <hr />
                            </div>
                        </div>
                    )}
                </form>
            ) : (
                <div className="loading-container">
                    <PageLoader />
                </div>
            )}
        </div>
    );
};

export default withAuthenticationRequired(AdminDataSync, {
    onRedirecting: () => <Loading />, 
});
